import { useIsFetching } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { useSession } from '../../lib/authClient.js';
import { ProductTour } from './ProductTour.js';
import type { TourHostView, TourView } from './tourSteps.js';
import { type TourApi, type TourHost, useProductTour } from './useProductTour.js';

const ROUTE_BY_VIEW = {
  home: '/dashboard',
  outcomes: '/outcomes',
  workflows: '/workflows',
  run: '/workflows',
} as const satisfies Record<TourView, string>;
const RUN_PATH = /^\/workflows\/([^/]+)/;
const WORKFLOW_CARDS_HIDDEN_TAB = 'runs';
const TourContext = createContext<TourApi | null>(null);

export interface TourLocation {
  view: TourHostView;
}

interface TourProviderProps {
  children: ReactNode;
}

export function tabOf(search: unknown): string | null {
  if (typeof search !== 'object' || search === null || !('tab' in search)) return null;
  return typeof search.tab === 'string' ? search.tab : null;
}

export function tourLocation(pathname: string, tab: string | null = null): TourLocation {
  if (RUN_PATH.test(pathname)) return { view: 'run' };
  if (pathname.startsWith('/dashboard')) return { view: 'home' };
  if (pathname.startsWith('/outcomes')) return { view: 'outcomes' };
  if (pathname.startsWith('/workflows')) {
    return { view: tab === WORKFLOW_CARDS_HIDDEN_TAB ? 'other' : 'workflows' };
  }
  return { view: 'other' };
}

export function shouldAutoStart(
  session: { data: unknown; isPending: boolean },
  fetching: number,
): boolean {
  return (
    session.data !== null && session.data !== undefined && !session.isPending && fetching === 0
  );
}

export function useTour(): TourApi | null {
  return useContext(TourContext);
}

export function TourProvider({ children }: TourProviderProps): ReactNode {
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  const tab = useLocation({ select: (location) => tabOf(location.search) });
  const location = useMemo(() => tourLocation(pathname, tab), [pathname, tab]);
  const session = useSession();
  const fetching = useIsFetching();
  const host = useMemo<TourHost>(
    () => ({
      ...location,
      setView: (view) => void navigate({ to: ROUTE_BY_VIEW[view] }),
    }),
    [location, navigate],
  );
  const tour = useProductTour(host, { autoStart: shouldAutoStart(session, fetching) });

  return (
    <TourContext.Provider value={tour}>
      {children}
      {tour.open ? <ProductTour key={pathname} tour={tour} /> : null}
    </TourContext.Provider>
  );
}
